import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Clock, Search, Loader2, Users as UsersIcon, Mail, Wand2, Calendar, Sparkles } from 'lucide-react';

// v5.3.5: Quick templates for weekday selection — applied per user row
const DAY_PRESETS = [
  { id: 'weekdays',     label: 'Solo entre semana (L-V)', days: { mon: true,  tue: true,  wed: true,  thu: true,  fri: true,  sat: false, sun: false } },
  { id: 'all',          label: 'Todos los días',           days: { mon: true,  tue: true,  wed: true,  thu: true,  fri: true,  sat: true,  sun: true  } },
  { id: 'mwf',          label: 'Lun-Mié-Vie',              days: { mon: true,  tue: false, wed: true,  thu: false, fri: true,  sat: false, sun: false } },
  { id: 'weekend',      label: 'Solo fines de semana',     days: { mon: false, tue: false, wed: false, thu: false, fri: false, sat: true,  sun: true  } },
  { id: 'none',         label: 'Ninguno (opt-out)',        days: { mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false } },
];

const ROLE_LABELS = {
  superadmin: 'Super Admin',
  admin: 'Admin',
  supervisor: 'Supervisor',
  user: 'Usuario',
};

const ROLE_COLORS = {
  superadmin: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
  admin: 'bg-sky-500/20 text-sky-300 border-sky-500/30',
  supervisor: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  user: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
};

const fmtHour = (h) => `${String(h).padStart(2, '0')}:00`;

const EmailSchedulePage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [bulkHour, setBulkHour] = useState(10);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [applyingBulk, setApplyingBulk] = useState(false);
  // v5.3.3: bulk weekday config
  const [bulkDaysOpen, setBulkDaysOpen] = useState(false);
  const [bulkDays, setBulkDays] = useState({
    mon: true, tue: true, wed: true, thu: true, fri: true, sat: false, sun: false,
  });
  const [applyingBulkDays, setApplyingBulkDays] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/email-schedule');
      setUsers(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo cargar la lista');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleHourChange = async (userId, newHour) => {
    setSavingId(userId);
    try {
      await api.put(`/users/${userId}/email-alert-hour`, {
        email_alert_hour: Number(newHour),
      });
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, email_alert_hour: Number(newHour) } : u))
      );
      toast.success(`Hora actualizada a ${fmtHour(newHour)} CDMX`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo actualizar');
    } finally {
      setSavingId(null);
    }
  };

  // v5.3.4: per-user weekday toggle (superadmin can edit any user)
  const handleToggleUserDay = async (userId, dayKey) => {
    const target = users.find((u) => u.id === userId);
    if (!target) return;
    const current = target.email_alert_days || {
      mon: true, tue: true, wed: true, thu: true, fri: true, sat: false, sun: false,
    };
    const next = { ...current, [dayKey]: !current[dayKey] };
    if (!Object.values(next).some(Boolean)) {
      toast.error('Al menos un día debe estar activo. Usa el preset "Ninguno" para opt-out total.');
      return;
    }
    // Optimistic UI
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, email_alert_days: next } : u)));
    setSavingId(userId);
    try {
      await api.put(`/users/${userId}/email-alert-days`, { email_alert_days: next });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo actualizar');
      // Revert
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, email_alert_days: current } : u)));
    } finally {
      setSavingId(null);
    }
  };

  // v5.3.5: Apply a quick template preset to a single user row
  const handleApplyPresetToUser = async (userId, preset) => {
    const target = users.find((u) => u.id === userId);
    if (!target) return;
    const previous = target.email_alert_days || {
      mon: true, tue: true, wed: true, thu: true, fri: true, sat: false, sun: false,
    };
    // Optimistic UI
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, email_alert_days: preset.days } : u)));
    setSavingId(userId);
    try {
      await api.put(`/users/${userId}/email-alert-days`, { email_alert_days: preset.days });
      if (preset.id === 'none') {
        toast.success(`${target.name || target.username}: opt-out — no recibirá correos automáticos`);
      } else {
        toast.success(`${target.name || target.username}: aplicado "${preset.label}"`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo aplicar el preset');
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, email_alert_days: previous } : u)));
    } finally {
      setSavingId(null);
    }
  };

  const handleApplyToAll = async () => {
    setApplyingBulk(true);
    try {
      const { data } = await api.put('/admin/email-schedule/bulk-set', {
        email_alert_hour: Number(bulkHour),
      });
      setUsers((prev) => prev.map((u) => ({ ...u, email_alert_hour: Number(bulkHour) })));
      toast.success(`${data.modified_count} usuarios ahora reciben correos a las ${fmtHour(bulkHour)} CDMX`);
      setBulkOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo aplicar');
    } finally {
      setApplyingBulk(false);
    }
  };

  const handleApplyDaysToAll = async () => {
    setApplyingBulkDays(true);
    try {
      const { data } = await api.put('/admin/email-schedule/bulk-set-days', {
        email_alert_days: bulkDays,
      });
      const activeKeys = Object.entries(bulkDays).filter(([, v]) => v).map(([k]) => k);
      const activeDays = activeKeys.length > 0 ? activeKeys.join(', ') : 'ninguno (opt-out total)';
      toast.success(`${data.modified_count} usuarios ahora reciben correos los días: ${activeDays}`);
      setBulkDaysOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo aplicar');
    } finally {
      setApplyingBulkDays(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [u.name, u.username, u.email, u.role, ROLE_LABELS[u.role]]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [users, search]);

  // Distribution summary (how many users per hour)
  const hourDistribution = useMemo(() => {
    const map = {};
    for (const u of users) {
      if (!u.email) continue;
      map[u.email_alert_hour] = (map[u.email_alert_hour] || 0) + 1;
    }
    return Object.entries(map)
      .map(([h, c]) => ({ hour: Number(h), count: c }))
      .sort((a, b) => a.hour - b.hour);
  }, [users]);

  const totalWithEmail = users.filter((u) => !!u.email).length;

  // v5.3.4: per-weekday coverage. Counts how many users with email DO NOT receive
  // emails on each weekday (i.e., have that day toggled OFF). Useful to detect
  // coverage gaps — e.g. "3 usuarios no reciben correos los martes".
  const _DAY_DEFS = [
    { key: 'mon', label: 'Lunes',     short: 'Lun', default: true },
    { key: 'tue', label: 'Martes',    short: 'Mar', default: true },
    { key: 'wed', label: 'Miércoles', short: 'Mié', default: true },
    { key: 'thu', label: 'Jueves',    short: 'Jue', default: true },
    { key: 'fri', label: 'Viernes',   short: 'Vie', default: true },
    { key: 'sat', label: 'Sábado',    short: 'Sáb', default: false },
    { key: 'sun', label: 'Domingo',   short: 'Dom', default: false },
  ];
  const dayCoverage = useMemo(() => {
    return _DAY_DEFS.map((d) => {
      const usersWithEmail = users.filter((u) => !!u.email);
      let receiving = 0;
      let notReceiving = 0;
      usersWithEmail.forEach((u) => {
        const days = u.email_alert_days || {
          mon: true, tue: true, wed: true, thu: true, fri: true, sat: false, sun: false,
        };
        if (days[d.key]) receiving += 1;
        else notReceiving += 1;
      });
      return { ...d, receiving, notReceiving };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  return (
    <div className="space-y-6" data-testid="email-schedule-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
          <Clock className="h-7 w-7 text-primary" />
          Horarios de envío de correos
        </h1>
        <p className="text-muted-foreground mt-1">
          Configura a qué hora (zona horaria Ciudad de México) recibe cada usuario los correos automáticos del sistema.
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card data-testid="summary-total-users">
          <CardHeader className="pb-2">
            <CardDescription>Usuarios con correo</CardDescription>
            <CardTitle className="text-3xl flex items-center gap-2">
              <UsersIcon className="h-6 w-6 text-sky-400" />
              {totalWithEmail}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card data-testid="summary-default-hour">
          <CardHeader className="pb-2">
            <CardDescription>Preferencia más común</CardDescription>
            <CardTitle className="text-3xl flex items-center gap-2">
              <Clock className="h-6 w-6 text-emerald-400" />
              {hourDistribution.length
                ? fmtHour(
                    hourDistribution.reduce(
                      (a, b) => (b.count > a.count ? b : a),
                      hourDistribution[0]
                    ).hour
                  )
                : '—'}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card data-testid="summary-hours-used">
          <CardHeader className="pb-2">
            <CardDescription>Horas distintas en uso</CardDescription>
            <CardTitle className="text-3xl flex items-center gap-2">
              <Mail className="h-6 w-6 text-amber-400" />
              {hourDistribution.length}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Distribution strip */}
      {hourDistribution.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Distribución por hora</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {hourDistribution.map((d) => (
                <Badge
                  key={d.hour}
                  variant="secondary"
                  className="text-xs"
                  data-testid={`hour-dist-${d.hour}`}
                >
                  {fmtHour(d.hour)} · {d.count} {d.count === 1 ? 'usuario' : 'usuarios'}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* v5.3.4: Day-of-week coverage card */}
      <Card data-testid="day-coverage-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Cobertura por día de la semana
          </CardTitle>
          <CardDescription>
            Cuántos usuarios reciben (o no) correos automáticos cada día. Útil para detectar lagunas en la cobertura.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {dayCoverage.map((d) => {
              const isWeekend = d.key === 'sat' || d.key === 'sun';
              const hasGap = !isWeekend && d.notReceiving > 0;
              return (
                <div
                  key={d.key}
                  data-testid={`day-coverage-${d.key}`}
                  className={`rounded-lg border p-3 text-center ${
                    hasGap
                      ? 'bg-amber-500/10 border-amber-500/30'
                      : isWeekend
                      ? 'bg-muted/40 border-border'
                      : 'bg-emerald-500/5 border-emerald-500/20'
                  }`}
                >
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    {d.label}
                  </div>
                  <div className={`text-2xl font-bold mt-1 ${hasGap ? 'text-amber-400' : 'text-foreground'}`}>
                    {d.receiving}
                  </div>
                  <div className="text-[10px] text-muted-foreground">de {totalWithEmail} reciben</div>
                  {d.notReceiving > 0 && (
                    <div className={`text-[11px] mt-1 ${hasGap ? 'text-amber-400 font-medium' : 'text-muted-foreground'}`}>
                      {d.notReceiving} {d.notReceiving === 1 ? 'usuario' : 'usuarios'} sin correo
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Users table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle>Usuarios</CardTitle>
              <CardDescription>
                Haz click en el selector para cambiar la hora preferida de cualquier usuario.
              </CardDescription>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <AlertDialog open={bulkOpen} onOpenChange={setBulkOpen}>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="bulk-apply-btn"
                    className="gap-2"
                  >
                    <Wand2 className="h-4 w-4" />
                    Aplicar a todos
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="bulk-apply-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Aplicar hora a todos los usuarios</AlertDialogTitle>
                    <AlertDialogDescription>
                      Esto sobrescribirá la hora preferida de <b>todos</b> los usuarios activos. Puedes ajustarlos individualmente después.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="space-y-2 py-2">
                    <label className="text-sm font-medium">Nueva hora para todos</label>
                    <Select value={String(bulkHour)} onValueChange={(v) => setBulkHour(Number(v))}>
                      <SelectTrigger data-testid="bulk-hour-select" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {Array.from({ length: 24 }, (_, h) => (
                          <SelectItem key={h} value={String(h)}>
                            {fmtHour(h)} hrs (CDMX)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={applyingBulk} data-testid="bulk-cancel">Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={(e) => { e.preventDefault(); handleApplyToAll(); }}
                      disabled={applyingBulk}
                      data-testid="bulk-confirm"
                    >
                      {applyingBulk && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Aplicar a todos
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {/* Bulk days dialog (v5.3.3) */}
              <AlertDialog open={bulkDaysOpen} onOpenChange={setBulkDaysOpen}>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="bulk-apply-days-btn"
                    className="gap-2"
                  >
                    <Wand2 className="h-4 w-4" />
                    Aplicar días a todos
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="bulk-apply-days-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Aplicar días de envío a todos</AlertDialogTitle>
                    <AlertDialogDescription>
                      Esto sobrescribirá los días de envío de correo de <b>todos</b> los usuarios activos. Por defecto Lun-Vie activos, Sáb-Dom apagados.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="space-y-3 py-2">
                    <div className="grid grid-cols-7 gap-2">
                      {[
                        { key: 'mon', label: 'Lun' },
                        { key: 'tue', label: 'Mar' },
                        { key: 'wed', label: 'Mié' },
                        { key: 'thu', label: 'Jue' },
                        { key: 'fri', label: 'Vie' },
                        { key: 'sat', label: 'Sáb' },
                        { key: 'sun', label: 'Dom' },
                      ].map(({ key, label }) => (
                        <button
                          key={key}
                          type="button"
                          data-testid={`bulk-day-${key}`}
                          onClick={() => setBulkDays((d) => ({ ...d, [key]: !d[key] }))}
                          className={`rounded-md border px-2 py-2 text-sm font-medium transition-all ${
                            bulkDays[key]
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-muted/40 text-muted-foreground border-border'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <div className="space-y-1.5">
                      <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <Sparkles className="h-3.5 w-3.5 text-primary" />
                        Plantillas rápidas
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {DAY_PRESETS.map((p) => (
                          <Button
                            key={p.id}
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            data-testid={`bulk-preset-${p.id}`}
                            onClick={() => setBulkDays(p.days)}
                          >
                            {p.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                    {!Object.values(bulkDays).some(Boolean) && (
                      <p className="text-xs text-amber-500">
                        Aviso: ningún día activo significa que <b>ningún usuario</b> recibirá correos automáticos (opt-out total).
                      </p>
                    )}
                  </div>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={applyingBulkDays}>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={(e) => { e.preventDefault(); handleApplyDaysToAll(); }}
                      disabled={applyingBulkDays}
                      data-testid="confirm-bulk-days-btn"
                    >
                      {applyingBulkDays ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Aplicar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <div className="relative w-full sm:w-72">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="email-schedule-search"
                  placeholder="Buscar por nombre, correo o rol..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              {search ? 'Sin resultados para tu búsqueda.' : 'No hay usuarios que mostrar.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Correo</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead className="text-right">Hora de envío</TableHead>
                    <TableHead className="text-center min-w-[280px]">Días de envío</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((u) => (
                    <TableRow key={u.id} data-testid={`email-row-${u.id}`}>
                      <TableCell>
                        <div className="font-medium">{u.name || u.username}</div>
                        <div className="text-xs text-muted-foreground">@{u.username}</div>
                      </TableCell>
                      <TableCell>
                        {u.email ? (
                          <span className="text-sm font-mono">{u.email}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">
                            sin correo configurado
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${ROLE_COLORS[u.role] || ROLE_COLORS.user} border`}>
                          {ROLE_LABELS[u.role] || u.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-2 justify-end">
                          {savingId === u.id && (
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          )}
                          <Select
                            value={String(u.email_alert_hour)}
                            onValueChange={(v) => handleHourChange(u.id, v)}
                            disabled={!u.email || savingId === u.id}
                          >
                            <SelectTrigger
                              data-testid={`hour-select-${u.id}`}
                              className="w-40"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                              {Array.from({ length: 24 }, (_, h) => (
                                <SelectItem key={h} value={String(h)}>
                                  {fmtHour(h)} hrs (CDMX)
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </TableCell>
                      <TableCell>
                        {u.email ? (
                          <div className="flex items-center gap-2 justify-center" data-testid={`days-row-${u.id}`}>
                            <div className="flex items-center gap-1">
                              {[
                                { key: 'mon', label: 'L' },
                                { key: 'tue', label: 'M' },
                                { key: 'wed', label: 'M' },
                                { key: 'thu', label: 'J' },
                                { key: 'fri', label: 'V' },
                                { key: 'sat', label: 'S' },
                                { key: 'sun', label: 'D' },
                              ].map(({ key, label }) => {
                                const days = u.email_alert_days || {
                                  mon: true, tue: true, wed: true, thu: true, fri: true, sat: false, sun: false,
                                };
                                const active = !!days[key];
                                return (
                                  <button
                                    key={key}
                                    type="button"
                                    data-testid={`day-toggle-${u.id}-${key}`}
                                    disabled={savingId === u.id}
                                    onClick={() => handleToggleUserDay(u.id, key)}
                                    title={`${active ? 'Desactivar' : 'Activar'} ${key}`}
                                    className={`w-7 h-7 text-xs font-semibold rounded-md border transition-all ${
                                      active
                                        ? 'bg-primary text-primary-foreground border-primary'
                                        : 'bg-muted/30 text-muted-foreground border-border hover:bg-muted'
                                    }`}
                                  >
                                    {label}
                                  </button>
                                );
                              })}
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-7 w-7"
                                  disabled={savingId === u.id}
                                  title="Plantillas rápidas"
                                  data-testid={`preset-trigger-${u.id}`}
                                >
                                  <Sparkles className="h-3.5 w-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel className="text-xs flex items-center gap-2">
                                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                                  Plantillas rápidas
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                {DAY_PRESETS.map((p) => (
                                  <DropdownMenuItem
                                    key={p.id}
                                    data-testid={`preset-${u.id}-${p.id}`}
                                    onClick={() => handleApplyPresetToUser(u.id, p)}
                                    className="text-sm"
                                  >
                                    {p.label}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground text-center">
        Los correos se envían en la zona horaria <span className="font-medium">America/Mexico_City</span>.
        Usuarios sin correo configurado no pueden recibir alertas automáticas.
      </p>
    </div>
  );
};

export default EmailSchedulePage;
